import React, { ReactNode, useEffect, useCallback, useState } from 'react';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAppDispatch } from '../store';
import LicenseBanner from '../components/shared/Licenses/LicenseBanner';
import LicenseOverlay from '../components/shared/Licenses/LicenseOverlay';
import NoSeatGate from '../components/shared/Licenses/NoSeatGate';

import { getPreferences, userInformation } from '../store/actions/DataAction';
import { hydrateUserPreferences } from '../store/actions/SettingsActions';
import { getAiModels, getUserID, getUserType } from '../store/actions/ChatAction';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { LayoutContext } from '../contexts/LayoutContext';
import { BreadcrumbsProvider } from '../contexts/BreadcrumbsContext';
import NotificationsSidepanel from '../components/Notifications/NotificationsSidepanel';

interface PrivateLayoutProps {
  children: ReactNode;
}

const PrivateLayout: React.FC<PrivateLayoutProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const userId = getUserID();
  const userType = getUserType();
  const history = useHistory();
  const [isContentScrollable, setIsContentScrollable] = useState<boolean>(true);

  useEffect(() => {
    if (userType) {
      const fetchData = async () => {
        await Promise.all([
          dispatch(getAiModels()),
          dispatch(userInformation()),
          dispatch(getPreferences()),
        ]);
        // AFTER the profile calls, not racing them: this decides whether the
        // AI preferences need fetching by looking at what is in the store, so
        // it has to see the store those calls leave behind. It is the single
        // app-init hydration of `ai_settings` — every service reads its
        // starting jurisdiction/language from the store it fills.
        await dispatch(hydrateUserPreferences() as any);
      };
      fetchData();
    }
  }, [userId, userType, dispatch]);

  const storeSession = useCallback((data: any) => {
    const { session_id } = data;
    if (session_id) {
      sessionStorage.setItem('session_id', session_id);
    }
  }, []);

  const sessionSignOut = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    history.push('/');
  }, [history]);

  // Socket listeners removed - legacy code


  return (
    <LayoutContext.Provider value={{ isContentScrollable, setIsContentScrollable }}>
      <BreadcrumbsProvider>
        {/*
          h-screen, NOT min-h-screen. This is THE height source for every page
          under it, and `min-height` is not a height: with it, nothing in the
          chain below is definite, so a page that says `h-full` resolves to
          `auto`, grows to its content, and takes the window scrollbar with it.
          Pages that own their own scroll (see `isContentScrollable`) then can't
          — their columns grow instead of scrolling. Measured: with min-height
          the settings shell rendered 4094px tall inside an 800px window and
          neither of its columns scrolled; with a definite height it pins to the
          box and each column scrolls on its own.

          Nothing here got shorter: `<main>` was already capped at 100vh, and
          the sidebar is already `h-screen`, so this container was 100vh in
          practice already. It just says so now.
        */}
        <div
          className={`
            flex flex-col h-screen
            app-ambience
            bg-background-light text-text-light
            dark:bg-background-dark dark:text-text-dark
            transition-colors duration-200
          `}
        >
          <div
            className={`
              flex-1 min-h-0 flex flex-row p-0 relative z-[1] overflow-hidden
              transition-all duration-200
            `}
          >
            <Sidebar />
            <main
              className={clsx(
                'flex-1 min-h-0',
                'custom-scrollbar-main relative z-0',
                // A page that has switched the page scroller off owns its own
                // height, so main becomes a flex column for it: the license
                // banner takes its band and the page takes the rest. Without
                // this the page is 100% of main and the banner pushes its
                // bottom out of an overflow-hidden box. Scrollable pages are
                // untouched — they still ride main's own scroller.
                isContentScrollable
                  ? 'overflow-y-auto max-h-[calc(100vh)]'
                  : 'overflow-hidden flex flex-col'
              )}
            >
              <LicenseBanner />
              <NoSeatGate>
                <LicenseOverlay>
                  {children}
                </LicenseOverlay>
              </NoSeatGate>
            </main>
            <NotificationsSidepanel />
          </div>
        </div>
      </BreadcrumbsProvider>
    </LayoutContext.Provider>
  );
};

export default PrivateLayout;
